import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { PSIC_COMPONENTS, initPsicIcons } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { Router, RouterOutlet, NavigationEnd, ActivatedRoute } from '@angular/router';
import { HeaderComponent } from './shared/header/header.component';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ChatbotWidgetComponent } from './chatbot-widget/chatbot-widget.component';
import { SelectionService } from './services/selection.service';
import { LoadingComponent } from './shared/loading/loading.component';
import { ToastComponent } from './shared/toast/toast.component';
import { Actions, ofType } from '@ngrx/effects';
import * as PolicyActions from './store/policy/policy.actions';
import { ConnectService } from './services/amazon-connect.service';
import { UserAuthenticatedComponent } from './user-authenticated/user-authenticated.component';
import { IvrProfileCardComponent } from './ivr-profile-card/ivr-profile-card.component';
import { BestMatchComponentComponent } from './best-match-component/best-match-component.component';
import { ChatbotStateService } from './shared/chatbot-state.service';
import { routeTransitionAnimation } from './animations';
import { globalMessages } from './shared/global-messages';
import { APP_CONFIG, AppConfig } from 'src/app.config';
import { OktaSessionService } from './services/okta-session.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    animations: [routeTransitionAnimation],
    imports: [
        CommonModule,
        RouterOutlet,
        HeaderComponent,
        ChatbotWidgetComponent,
        MatIcon,
        LoadingComponent,
        ToastComponent,
        ...PSIC_COMPONENTS,
        UserAuthenticatedComponent,
        IvrProfileCardComponent,
        BestMatchComponentComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit, OnDestroy {
    currentFlag: boolean = false;
    sessionId = '';
    showHeader = false;
    showChatWidget = false;
    isThirdParty = false;
    isLandingPage = true;
    private _contactIdOverride: string | null = null;
    private _channelOverride: string | null = null;
    get contactId(): string {
        return this._contactIdOverride || this.connectService.contactId || '';
    }
    get channel(): string {
        return this._channelOverride || this.connectService.channel || 'voice';
    }
    private queryParamsSubscription?: Subscription;
    get attributes(): any {
        return this.connectService.attributes ?? {};
    }
    isNavOpen = false;
    isChatbotExpanded = false;
    chatbotWidth = 354;
    // for user authenticated card
    highlightedSection: string = '';
    showBanner = true;
    isProfileNavOpen: Boolean = false;

    dismissBanner() {
        this.showBanner = false;
    }

    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private selectionService: SelectionService,
        private router: Router,
        private renderer: Renderer2,
        private actions$: Actions,
        private activatedRoute: ActivatedRoute,
        private connectService: ConnectService,
        private chatbotStateService: ChatbotStateService,
        private oktaSessionService: OktaSessionService,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const noHeaderRoutes = ['login', 'third-party-login', 'contact-us'];
                const isNoHeaderRoute = noHeaderRoutes.some((route) => event.url.includes(route));
                this.showHeader = !isNoHeaderRoute;
                this.showChatWidget = !isNoHeaderRoute;
                this.isThirdParty = event.url.includes('third-party');

                // Hide landing page for routes that have their own full-page content
                const landingPageRoutes = ['profile-activities', 'policy360', 'call-intent'];
                if (landingPageRoutes.some((route) => event.url.includes(route))) {
                    const hasPolicyNumber = event.url.includes('policyNumber=');
                    this.isLandingPage = !hasPolicyNumber;
                    this.selectionService.setIsLandingPage(!hasPolicyNumber);
                }
                // call-intent always hides the landing page
                if (event.url.includes('call-intent')) {
                    this.isLandingPage = false;
                    this.selectionService.setIsLandingPage(false);
                }

                // If isThirdParty is true, then isLandingPage should be false
                if (this.isThirdParty) {
                    this.showChatbot = false;
                    this.showChatWidget = false;
                    this.isLandingPage = false;
                    this.selectionService.setIsLandingPage(false);
                }

                // After navigation, re-apply the current chatbot width to the newly-rendered
                // page container so it immediately respects the resized panel width.
                if (this.showChatbot) {
                    // Use setTimeout(0) to let Angular finish rendering the new route component
                    setTimeout(() => this.chatbotStateService.applyCurrentWidthToDOM(), 0);
                }
            }
        });

        // Theme setup
        const storedTheme = localStorage.getItem('selectedTheme');
        if (storedTheme && !this.config.production) {
            this.selectedTheme = storedTheme;
        }
        this.applyTheme(this.selectedTheme);

        // iconReg.addSvgIconResolver((name: string) =>
        //     sanitizer.bypassSecurityTrustResourceUrl(`/assets/images/themes/${this.selectedTheme}/${name}.svg`)
        // );

        initPsicIcons(domSanitizer, matIconRegistry);

        matIconRegistry.addSvgIconResolver((name: string) =>
            domSanitizer.bypassSecurityTrustResourceUrl(`/assets/images/${name}.svg`)
        );

        // Icons are resolved via the app's `MatIconRegistry` resolver above.

        this.matIconRegistry.addSvgIcon(
            'custom-message',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/uil-message.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'custom-ai-avatar',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/Assistant-favicon.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'custom-user-avatar',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/Avatar.svg')
        );
    }

    ngOnInit(): void {
         // Start watching Okta token lifecycle so the app auto-logs-out on expiry.
        this.oktaSessionService.start();

        this.connectService.init(); // for testing purpose
        this.chatbotStateService.width$.subscribe((width) => {
            this.chatbotWidth = width;
            this.isChatbotExpanded = width > 354;
        });
        if (this.isLandingPage) {
            this.openChatbot();
        }

        this.selectionService.selectedItem$.subscribe((policyValue) => {
            if (policyValue) {
                this.sessionId = policyValue;
            }
        });
        this.actions$.pipe(ofType(PolicyActions.loadPolicyData)).subscribe(() => {
            this.isLandingPage = false;
            this.selectionService.setIsLandingPage(false);
            if (!this.isThirdParty) {
                this.openChatbot();
            }
        });

        this.queryParamsSubscription = this.activatedRoute.queryParams.subscribe((params) => {
            if (params['contactId']) this._contactIdOverride = params['contactId'];
            if (params['contactChannel']) this._channelOverride = params['contactChannel'];
        });
    }

    showChatbot = true;

    openChatbot() {
        this.showChatbot = true;
        // Reset panel width to minimum and apply to all page containers
        this.chatbotStateService.setWidth(354);
        // Let Angular render the chatbot widget first, then re-apply so the
        // newly-mounted containers also get the correct right offset
        setTimeout(() => this.chatbotStateService.applyCurrentWidthToDOM(), 0);
    }

    ngOnDestroy(): void {
        this.queryParamsSubscription?.unsubscribe();
        // Stop the Okta session watcher to clear its interval timer and
        // storage event listener, preventing memory leaks after destruction.
        this.oktaSessionService.stop();
    }

    onProfileNavToggled(isOpen: boolean) {
        this.isProfileNavOpen = isOpen;
    }
    onChatbotClose() {
        this.showChatbot = false;
        window.dispatchEvent(new CustomEvent('chatbotClosed'));
        // Clear the right offset on all page containers so they fill the full width
        this.chatbotStateService.clearPageContainerOffsets();
    }

    //landing-page-header
    isRotated = false;
    chatHistory = [
        {
            label: 'Recent conversations',
            chatDescriptions: [],
            isRotated: false,
        },
        {
            label: 'Last 7 days',
            chatDescriptions: [],
            isRotated: false,
        },
        {
            label: 'Last 30 days',
            chatDescriptions: [],
            isRotated: false,
        },
    ];

    toggleRotation(index: number) {
        this.chatHistory[index].isRotated = !this.chatHistory[index].isRotated;
    }

    selectedTheme: string = this.config.theme;
    onThemeChange(theme: string) {
        this.selectedTheme = theme;
        this.applyTheme(theme);
        window.location.reload();
    }

    private applyTheme(theme: string) {
        globalMessages.themeList.forEach((className) => document.body.classList.remove(className));
        this.renderer.addClass(document.body, theme);
    }
}
